import { Controller } from 'stimulus';

/**
 * Reveals its `fieldset` targets once every one of its `checkbox` targets is
 * checked, and hides them again as soon as one is unticked.
 *
 * For a section that only makes sense when the whole set of boxes is ticked.
 * `fieldset-visibility` keys on one field having a value, which a group of
 * checkboxes cannot answer — "all of them" is the condition here.
 *
 * Usage:
 * - on each checkbox: data-all-checked-fieldset-target="checkbox" and
 *   data-action="change->all-checked-fieldset#toggle"
 * - on the section to reveal: data-all-checked-fieldset-target="fieldset"
 * - on a section the same condition takes away:
 *   data-all-checked-fieldset-target="invertedFieldset" — for a switch that
 *   replaces one part of a form with another, so the two can never be submitted
 *   together.
 *
 * The two targets are treated differently on purpose. A `fieldset` target is the
 * condition's own section, so it comes and goes with it. An `invertedFieldset`
 * target is a section the form is about either way — the switch only takes it out
 * of this submission — so it stays on the page and locks, which also keeps the
 * revealed section from moving out from under the cursor.
 *
 * The closed state belongs to the server, which renders it `disabled` (plus
 * `d-none` on a `fieldset` target) so nothing inside an unrevealed section is
 * submitted; this only keeps it in step with the boxes. Disabling the section
 * itself (rather than the inputs in it) leaves any nested fieldset's own disabled
 * state alone, so a field waiting on its own condition stays out of reach when the
 * section opens.
 */
export default class extends Controller {
  static targets = ['checkbox', 'fieldset', 'invertedFieldset'];

  connect() {
    this.toggle();
  }

  toggle() {
    const revealed = this.allChecked;

    this.fieldsetTargets.forEach((fieldset) => this.reveal(fieldset, revealed));
    this.invertedFieldsetTargets.forEach((fieldset) =>
      this.lock(fieldset, revealed)
    );
  }

  reveal(fieldset, revealed) {
    fieldset.classList.toggle('d-none', !revealed);
    this.lock(fieldset, !revealed);
  }

  lock(fieldset, locked) {
    fieldset.disabled = locked;
  }

  // Nothing to tick is not a complete set: a form offering no checkboxes has
  // nothing that could reveal the section.
  get allChecked() {
    return (
      this.checkboxTargets.length > 0 &&
      this.checkboxTargets.every((checkbox) => checkbox.checked)
    );
  }
}
